import { useCallback } from 'react';

import type { OnSubmitData, PendingInviteUser } from '@atlassian/invite-people/types';

import { useAnalytics } from '@townsquare/analytics';

import { jiraRestClient } from '../clients/JiraRestClient';
import type { InviteStatus, InviteToProjectResponse } from '../clients/JiraRestClient';
import type { JiraRoleDetail, JiraSpaceOption, JiraSpaceStyles, JiraSpaceTypes } from '../types/SpaceInputTypes';

export type InviteResult = {
	email: string;
	response: InviteToProjectResponse;
};

export type InviteError = {
	email: string;
	inviteeAaId?: string;
	error?: Error;
	status?: InviteStatus;
};

export type InviteToProjectResult = {
	successfulInvites: InviteResult[];
	failedInvites: InviteError[];
};

export type InviteAnalyticsContext = {
	source: string;
	roleId?: number;
	projectId: string;
	spaceSource?: string;
	spaceType?: JiraSpaceTypes;
	spaceStyle?: JiraSpaceStyles;
	edition?: string;
	isDefaultRole?: boolean;
	isAdminRole?: boolean;
	isAdmin: boolean;
	atlOriginAttributes: object;
};

type InviteApiArgs = {
	cloudId: string;
	selectedSpace: JiraSpaceOption;
	selectedRole?: JiraRoleDetail;
	atlOrigin: string;
	continueUrl: string;
	getRecaptchaToken?: OnSubmitData['getRecaptchaToken'];
};

type InviteUserArguments = InviteApiArgs & {
	user: PendingInviteUser;
};

const isErrorStatus = (status: InviteStatus): boolean => {
	return status === 'NOT_INVITED' || status === 'LICENSE_LIMIT_REACHED';
};

const inviteUser = async ({
	user,
	cloudId,
	selectedSpace,
	selectedRole,
	atlOrigin,
	continueUrl,
	getRecaptchaToken,
}: InviteUserArguments): Promise<InviteResult> => {
	const email = user.email ?? user.id;
	const recaptchaToken = await getRecaptchaToken?.('v1_invitations_invite').catch(() => undefined);

	const response = await jiraRestClient.requestSpaceAccessForUser({
		cloudId,
		projectId: selectedSpace.id,
		payload: {
			email,
			projectRoleIds: selectedRole ? [selectedRole.id] : [],
			atlOrigin,
			continueUrl,
		},
		recaptchaToken,
	});

	return { email, response };
};

/**
 * Processes invites sequentially, one at a time.
 * Used for free edition to avoid race conditions with user limits.
 */
const processSequentialInvites = async (
	users: PendingInviteUser[],
	apiArgs: InviteApiArgs,
	onSuccess: (response: InviteToProjectResponse) => void,
	onFailed: (message: string) => void,
): Promise<InviteToProjectResult> => {
	const result: InviteToProjectResult = {
		successfulInvites: [],
		failedInvites: [],
	};

	for (const user of users) {
		try {
			const inviteResult = await inviteUser({ ...apiArgs, user });
			const { response } = inviteResult;

			if (isErrorStatus(response.inviteStatus)) {
				result.failedInvites.push({
					email: inviteResult.email,
					status: response.inviteStatus,
				});
				onFailed(`Error Response Status: ${response.inviteStatus}`);
			} else {
				result.successfulInvites.push(inviteResult);
				onSuccess(inviteResult.response);
			}
		} catch (error) {
			const errorObject = error instanceof Error ? error : new Error(String(error));
			result.failedInvites.push({
				email: user.email ?? user.id,
				error: errorObject,
			});
			onFailed(`Error: ${errorObject.message}`);
		}
	}

	return result;
};

const processParallelInvites = async (
	users: PendingInviteUser[],
	apiArgs: InviteApiArgs,
	onSuccess: (response: InviteToProjectResponse) => void,
	onFailed: (message: string) => void,
): Promise<InviteToProjectResult> => {
	const promises = users.map(user => inviteUser({ ...apiArgs, user }));
	const settledResults = await Promise.allSettled(promises);

	const result: InviteToProjectResult = {
		successfulInvites: [],
		failedInvites: [],
	};

	settledResults.forEach((settledResult, index) => {
		if (settledResult.status === 'fulfilled') {
			const inviteResult = settledResult.value;
			const { response } = inviteResult;

			if (isErrorStatus(response.inviteStatus)) {
				result.failedInvites.push({
					email: inviteResult.email,
					status: response.inviteStatus,
				});
				onFailed(`Error Response Status: ${response.inviteStatus}`);
			} else {
				result.successfulInvites.push(inviteResult);
				onSuccess(inviteResult.response);
			}
		} else {
			const errorObject =
				settledResult.reason instanceof Error ? settledResult.reason : new Error(String(settledResult.reason));
			result.failedInvites.push({
				email: users[index].email ?? users[index].id,
				error: errorObject,
			});
			onFailed(`Error: ${errorObject.message}`);
		}
	});

	return result;
};

export const useInviteToProject = () => {
	const analytics = useAnalytics();

	return useCallback(
		async (
			usersToInvite: PendingInviteUser[],
			apiArgs: InviteApiArgs,
			isFreeEdition: boolean,
			analyticsContext: InviteAnalyticsContext,
		): Promise<InviteToProjectResult> => {
			const onSuccess = (response: InviteToProjectResponse): void => {
				const { inviteeAaId, inviteStatus } = response;

				void analytics.track('requestAccess', 'succeeded', {
					...response,
					...analyticsContext,
				});

				if (inviteStatus === 'INVITED' || inviteStatus === 'USER_EXISTS') {
					void analytics.track('user', 'invited', {
						invitedUserId: inviteeAaId,
						inviteStatus,
						...analyticsContext,
					});
				} else if (inviteStatus === 'INVITED_PENDING_APPROVAL' || inviteStatus === 'PENDING_INVITE_EXISTS') {
					void analytics.track('productAccess', 'requested', {
						requestedForUserId: inviteeAaId,
						inviteStatus,
						exists: inviteStatus === 'PENDING_INVITE_EXISTS',
						...analyticsContext,
					});
				}
			};

			const onFailed = (message: string): void => {
				void analytics.track('requestAccess', 'failed', { ...analyticsContext, message });
			};

			if (isFreeEdition) {
				// REST calls are run sequentially for Free to make sure the user limit is not exceeded
				// due to a race condition.
				return processSequentialInvites(usersToInvite, apiArgs, onSuccess, onFailed);
			}

			return processParallelInvites(usersToInvite, apiArgs, onSuccess, onFailed);
		},
		[analytics],
	);
};
